<template>
  <div class="max-w-3xl mx-auto p-6">
    <!-- Check if user is organizer -->
    <div v-if="!isOrganizer" class="text-center py-12">
      <div class="text-red-600 mb-4">
        <span class="text-4xl">⚠️</span>
      </div>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">Endast för organisatörer</h3>
      <p class="text-gray-600 mb-4">Du måste vara registrerad som evenemangsorganisatör för att skapa evenemang.</p>
      <router-link 
        to="/register"
        class="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
      >
        Registrera som organisatör
      </router-link>
    </div>

    <div v-else>
      <h1 class="text-3xl font-bold mb-6">Skapa Event</h1>

         <form @submit.prevent="createEvent" class="space-y-6">
      <div>
        <label class="block font-semibold mb-1">Titel</label>
        <input v-model="title" placeholder="Ex. Sommargala" required class="input" />
      </div>

      <div>
        <label class="block font-semibold mb-1">Beskrivning</label>
        <textarea v-model="description" placeholder="Beskriv eventet..." required class="input" rows="3" />
      </div>

      <div>
        <label class="block font-semibold mb-1">Sök plats</label>
        <v-autocomplete
          v-model="selectedLocation"
          :items="searchResults"
          :loading="isSearching"
          v-model:search="addressQuery"
          label="Sök plats..."
          item-title="display_name"
          return-object
          hide-no-data
          hide-details
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props">
              <v-list-item-title>{{ item.display_name }}</v-list-item-title>
              <v-list-item-subtitle>Lat: {{ item.lat }}, Lon: {{ item.lon }}</v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-autocomplete>
        <label for="event-location" class="mt-3 block text-sm font-medium text-gray-700">
          Plats för evenemanget
        </label>
        <input
          id="event-location"
          v-model.trim="location"
          required
          maxlength="300"
          placeholder="Skriv plats eller adress"
          class="input mt-1"
        />
        <p class="mt-1 text-sm text-gray-500">
          Du kan skriva platsen själv eller välja ett förslag ovan.
        </p>
      </div>

      <div>
        <label class="block font-semibold mb-1">Datum & tid</label>
        <input v-model="date" type="datetime-local" required class="input" />
      </div>

      <div>
        <label class="block font-semibold mb-1">Omslagsbild</label>
        <input type="file" @change="handleImageUpload" accept="image/*" />
        <p v-if="imageError" role="alert" class="mt-2 text-sm text-red-600">{{ imageError }}</p>
        <div v-if="previewImage" class="mt-4">
          <img :src="previewImage" alt="Förhandsvisning av omslagsbild" class="w-full h-64 object-cover rounded-lg shadow-md" />
        </div>
      </div>

      <div>
        <label class="block font-semibold mb-2">Biljetter</label>
        <div v-for="(ticket, index) in tickets" :key="index" class="border p-4 rounded mb-4 bg-gray-50 relative">
          <div class="mb-2">
            <label class="text-sm font-medium block">Biljettnamn</label>
            <input v-model="ticket.name" placeholder="Ex. Early Bird" class="input" />
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-medium block">Pris (kr)</label>
              <input v-model.number="ticket.price" type="number" min="0" class="input" />
            </div>
            <div>
              <label class="text-sm font-medium block">Antal biljetter</label>
              <input v-model.number="ticket.quantity" type="number" min="0" class="input" />
            </div>
          </div>
          <button v-if="tickets.length > 1" @click="removeTicket(index)" type="button" class="absolute top-2 right-2 text-red-500 hover:underline text-sm">Ta bort</button>
        </div>
        <button type="button" @click="addTicket" class="text-indigo-600 hover:underline">+ Lägg till biljett</button>
      </div>

      <p v-if="submitError" role="alert" class="rounded-md bg-red-50 p-3 text-red-700">
        {{ submitError }}
      </p>

      <button type="submit" :disabled="saving" class="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 w-full disabled:opacity-60">
        {{ saving ? 'Skapar event...' : 'Skapa Event' }}
      </button>
    </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

let fetchTimeout = null;

export default {
  computed: {
    isOrganizer() {
      const user = this.$store.state.user;
      return !!(user && (user.isOrganizer || user.role === 'organizer' || user.role === 'admin'));
    }
  },
  data() {
    return {
      title: '',
      description: '',
      location: '',
      latitude: null,
      longitude: null,
      date: new Date().toISOString().slice(0, 16),
      imageFile: null,
      previewImage: null,
      imageError: '',
      submitError: '',
      saving: false,
      tickets: [{ name: 'Early Bird', price: 0, quantity: 0 }],
      addressQuery: '',
      searchResults: [],
      selectedLocation: null,
      isSearching: false,
    };
  },
  watch: {
    addressQuery(newVal) {
      console.log('Söker efter:', newVal); // <- denna ska loggas

      if (fetchTimeout) clearTimeout(fetchTimeout);
      if (!newVal) {
        this.searchResults = [];
        return;
      }
      fetchTimeout = setTimeout(() => {
        this.fetchLocations(newVal);
      }, 300);
    },
    selectedLocation(place) {
      if (place && typeof place === 'object') {
        this.selectPlace(place);
      }
    },
  },
  methods: {
    handleImageUpload(event) {
       const file = event.target.files?.[0];
       if (file) {
         this.imageError = '';
         // Check file size (max 5MB)
         const maxSize = 5 * 1024 * 1024; // 5MB in bytes
         if (file.size > maxSize) {
           this.imageError = 'Filen är för stor. Välj en bild som är mindre än 5 MB.';
           event.target.value = '';
           return;
         }
         
         // Check file type
         if (!file.type.startsWith('image/')) {
           this.imageError = 'Välj en giltig bildfil.';
           event.target.value = '';
           return;
         }
         
         this.imageFile = file;
         if (this.previewImage) URL.revokeObjectURL(this.previewImage);
         this.previewImage = URL.createObjectURL(file);
       }
     },
    addTicket() {
      this.tickets.push({ name: '', price: 0, quantity: 0 });
    },
    removeTicket(index) {
      this.tickets.splice(index, 1);
    },
    async fetchLocations(query) {
      this.isSearching = true;
      try {
        const res = await fetch(`/api/map/places?q=${encodeURIComponent(query)}`);
        this.searchResults = await res.json();
      } catch (e) {
        console.error('Adressökning misslyckades:', e);
      } finally {
        this.isSearching = false;
      }
    },
         selectPlace(place) {
       console.log('Selected place:', place);
       this.location = place.display_name;
       this.latitude = place.lat;
       this.longitude = place.lon;
       this.addressQuery = place.display_name;
       console.log('Updated location data:', {
         location: this.location,
         latitude: this.latitude,
         longitude: this.longitude
       });
          },
     compressImage(file) {
       return new Promise((resolve, reject) => {
         const canvas = document.createElement('canvas');
         const ctx = canvas.getContext('2d');
         const img = new Image();
         
         img.onload = () => {
           // Calculate new dimensions (max 800px width/height)
           const maxSize = 800;
           let { width, height } = img;
           
           if (width > height) {
             if (width > maxSize) {
               height = (height * maxSize) / width;
               width = maxSize;
             }
           } else {
             if (height > maxSize) {
               width = (width * maxSize) / height;
               height = maxSize;
             }
           }
           
           // Set canvas dimensions
           canvas.width = width;
           canvas.height = height;
           
           // Draw and compress image
           ctx.drawImage(img, 0, 0, width, height);
           
           // Convert to base64 with quality 0.7 (70%)
           const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
           
           // Check if the compressed image is still too large (> 500KB)
           const base64Size = compressedDataUrl.length * 0.75; // Approximate size in bytes
           if (base64Size > 500 * 1024) {
             // Further compress with lower quality
             const furtherCompressed = canvas.toDataURL('image/jpeg', 0.5);
             resolve(furtherCompressed);
           } else {
             resolve(compressedDataUrl);
           }
         };
         
         img.onerror = reject;
         img.src = URL.createObjectURL(file);
       });
     },
     async createEvent() {
       this.submitError = '';
       if (!this.location.trim()) {
         this.submitError = 'Ange en plats för evenemanget.';
         return;
       }
       this.saving = true;
       try {
         console.log('=== CREATE EVENT START ===');
         console.log('Form data:', {
           title: this.title,
           description: this.description,
           location: this.location,
           date: this.date,
           latitude: this.latitude,
           longitude: this.longitude,
           tickets: this.tickets
         });

         // Convert image to base64 if present
         let imageData = null;
         if (this.imageFile) {
           console.log('Compressing image...');
           console.log('🔍 Image file info:', {
             name: this.imageFile.name,
             size: this.imageFile.size,
             type: this.imageFile.type
           });
           // Compress image before converting to base64
           imageData = await this.compressImage(this.imageFile);
           console.log('Image compressed successfully');
           console.log('🔍 Compressed image info:', {
             hasImageData: !!imageData,
             imageDataLength: imageData ? imageData.length : 0,
             imageDataStart: imageData ? imageData.substring(0, 50) + '...' : 'none'
           });
         } else {
           console.log('No image file selected');
         }

         const eventData = {
           title: this.title,
           description: this.description,
           location: this.location,
           date: this.date,
           tickets: JSON.stringify(this.tickets),
           imageData: imageData
         };

         if (this.latitude !== null && this.longitude !== null) {
           eventData.lat = this.latitude;
           eventData.lng = this.longitude;
         }

         console.log('Sending event data:', eventData);
         console.log('🔍 Final event data check:', {
           title: eventData.title,
           hasImageData: !!eventData.imageData,
           imageDataLength: eventData.imageData ? eventData.imageData.length : 0,
           imageDataStart: eventData.imageData ? eventData.imageData.substring(0, 50) + '...' : 'none'
         });

        await axios.post('/api/events/create', eventData, {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        });

        this.$router.push('/events');
      } catch (error) {
        console.error('❌ Event creation failed:', error.response?.data || error.message);
        this.submitError = error.response?.data?.message ||
          'Det gick inte att skapa eventet. Kontrollera anslutningen och försök igen.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 0.375rem;
  outline: none;
}
.input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px #6366f1;
}
</style>
